<div
  class="knots py-2 px-4"
  appDroppable
  appDroppableHoverClass="drag-over"
  [appDroppablePredicate]="labelDropPredicate"
  (appDroppableDrop)="dropLabels($event)"
>
  <div
    class="knots-content"
    [appExpandableList]="expanded()"
    [collapsedRows]="2"
    [reserveEnd]="56"
    (expandableMorePosition)="morePosition = $event"
    (expandableOverflowChange)="hasOverflow = $event"
  >
    @if (!withPlaceholder || knots().length > 0) {
      <!-- stitch-app-shell Knowledge box: a small type icon leads the row; its tooltip names it. -->
      <span class="knowledge-row-icon" role="img" aria-label="Knots" title="Knots">
        <app-icon name="sapphire-knots" [size]="16" />
      </span>
    }

    @if (withPlaceholder && knots().length === 0) {
      <div class="knots-placeholder">No knots were extracted</div>
    }

    <!-- Shared pages: sort/filter stays visible but non-interactive (sorting drops markers — SBUX-539).
         Ahead of the add: both float right, so the + lands to the filter's left. -->
    <app-context-menu-state-indicator
      class="knots-icon no-width print:!hidden"
      matTooltip="Sort & filter"
      [class.pointer-events-none]="isShared"
      svgIcon="filters"
      [appKnotsManageContextMenu]="false"
      [appKnotsManageContextMenuStateKey]="stateKey"
      [appKnotsManageContextMenuHubMode]="isHubMode"
      [appKnotsManageContextMenuHasItems]="stitchItems.length > 0"
      (appKnotsManageContextMenuStateChange)="setState($event)"
    />

    @if (withControls && !isShared) {
      <!-- [NAV-021] 'Manage my knots' icon removed per task; uncomment (with #manageForm below) to re-enable
      <app-icon
        class="knots-icon print:!hidden"
        name="manage"
        stchPopover
        [stchPopoverArrow]="true"
        [stchPopoverContent]="manageForm"
        stchPopoverTrigger="click"
        stchPopoverPlacement="right"
        [stchPopoverShowUntil]="hidePopover"
      />
      -->

      <!-- Add control is dropped on shared pages; only item-info pages can add knots. -->
      @if (knots().length === 0 && !loading()) {
        <!-- stitch-app-shell Knowledge box: an empty row keeps its dashed "+ Add knots" showing (not while the
             skeleton chips stand in for knots that are still loading). -->
        <button
          type="button"
          class="knowledge-add-pill print:!hidden"
          stchPopover
          [stchPopoverArrow]="true"
          [stchPopoverContent]="addForm"
          stchPopoverTrigger="click"
          stchPopoverPlacement="right"
          [stchPopoverShowUntil]="hidePopover"
        >
          <app-icon name="sapphire-plus" [size]="12" />
          Add knots
        </button>
      } @else {
        <!-- stitch-app-shell Knowledge box AddIcon: the dashed-circle plus, on the filter icon's hover plate. -->
        <button
          type="button"
          class="knots-icon no-width knowledge-add-icon print:!hidden"
          aria-label="Add knots"
          matTooltip="Add knots"
          stchPopover
          [stchPopoverArrow]="true"
          [stchPopoverContent]="addForm"
          stchPopoverTrigger="click"
          stchPopoverPlacement="right"
          [stchPopoverShowUntil]="hidePopover"
        >
          <span class="knowledge-add-icon-plate"><app-icon name="sapphire-plus-circle-dashed" [size]="22" /></span>
        </button>
      }
    }

    <!-- [MUST-003] 'Debug' button hidden per task; uncomment to re-enable
    @if(withControls) {
      <div class="knots-icon no-width debug print:!hidden" [class.active]="debug" (click)="doDebug()">
        {{ debug || 'Debug' }}
      </div>
    }
    -->

    <!-- SBUX-1110: skeleton chips instead of an empty row while a saved item's knots load. The skin draws them
         as the Knowledge box's 20px round pills. -->
    @if (loading() && !withPlaceholder && !isShared) {
      <span data-testid="knots-skeleton">
        <stch-skeleton-block class="mr-2 inline-block h-5 w-32 rounded-full align-middle bg-blue-200/60" />
        <stch-skeleton-block class="inline-block h-5 w-24 rounded-full align-middle bg-blue-200/60" />
      </span>
    }

    <!--
      The chip acts, so the thing a person clicks is a real button and the chip is what it contains -
      `CLAUDE.rules.md` §4e-quater. The NAME is given rather than taken from the content: Chrome exposes
      `app-knot`'s own root as `group`, a role with no name-from-content, so a button wrapping it computes
      an EMPTY name. The chip binds no `withRemove` here, so nothing interactive is nested inside.
      (`#1146` round 4; the rail settled this shape in round 3.)

      DISABLED on a shared page, because there the control does nothing: `handleClick` returns at
      once, and `SharedKnotsComponent` compiles this very template onto a public share link. A
      focusable button that announces a state and refuses to act is what `#1127` legislated
      against, so the control carries the truth instead. (`#1174` review F1.)
    -->
    @for (knot of displayedKnots(); track knot.name) {
      <button
        type="button"
        class="knots-item knots-activator mr-2"
        [disabled]="isShared"
        [attr.aria-label]="knot?.name || 'knot'"
        [attr.aria-pressed]="selectedKnots() | includesBy : knot?.name : 'name'"
        (click)="handleClick(knot, $event)"
      >
        <app-knot
          [knot]="knot"
          [withDetach]="!isShared"
          [withControls]="withControls && !isShared"
          [dragEnabled]="!isShared"
          [selected]="selectedKnots() | includesBy : knot?.name : 'name'"
          [debug]="debug"
          [tenantId]="owningTenantId"
          (detach)="handleDetach(knot)"
          (delete)="handleDelete(knot)"
          (pin)="handlePin(knot)"
        />
      </button>
    }
  </div>

  @if (expanded()) {
    <button type="button" class="knots-item knots-more" (click)="showLess()">Less</button>
    @if (page() < pagesCount - 1 && knots().length) {
      <button type="button" class="knots-item knots-more" (click)="showMore()">More</button>
    }
  } @else if ((hasOverflow || page() < pagesCount - 1)) {
    <!-- On the second line, right after the last chip it leaves showing (Johanna, 2026-10-21). -->
    <button
      type="button"
      class="knots-item knots-more knots-more-inline"
      [class.placed]="!!morePosition"
      [style.left.px]="morePosition?.left"
      [style.top.px]="morePosition?.top"
      [style.height.px]="morePosition?.height"
      (click)="showMore()"
    >
      More
    </button>
  }
</div>

<!-- [NAV-021] 'Manage my knots' form removed per task; uncomment (with the icon above) to re-enable
<ng-template #manageForm>
  <app-manage-knots-form
    [existingKnots]="knots()"
    (close)="closePopovers()"
    (save)="saveKnots($event)"
  />
</ng-template>
-->

<ng-template #addForm>
  <app-add-knots
    [stitchItems]="stitchItems"
    [tenantId]="owningTenantId"
    (close)="closePopovers()"
    (save)="saveKnots($event)"
  />
</ng-template>
